import { useState, useEffect, useRef } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { FaceSmileIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const MAX_MESSAGE_LENGTH = 500; // Maximum character count for messages

function MessageInput({ onSendMessage, onTyping }) {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  
  const characterCount = message.length;
  const isNearLimit = characterCount > MAX_MESSAGE_LENGTH * 0.8;
  const isAtLimit = characterCount >= MAX_MESSAGE_LENGTH;

  // Handle typing indicator
  useEffect(() => {
    if (message && !isTyping) {
      setIsTyping(true);
      // Notify parent component about typing status
      if (onTyping) onTyping(true);
    }
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        if (onTyping) onTyping(false);
      }
    }, 2000);
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, isTyping, onTyping]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && characterCount <= MAX_MESSAGE_LENGTH) {
      onSendMessage(message);
      setMessage('');
      setIsTyping(false);
      if (onTyping) onTyping(false);

      // Log message sending
      console.log("Message sent:", message);
    }
  };

  const onEmojiClick = (emojiData) => {
    if (characterCount < MAX_MESSAGE_LENGTH) {
      setMessage(prev => prev + emojiData.emoji);
    }
  };

  const handleKeyPress = (e) => {
    // Submit on Enter, but allow Shift+Enter for new lines
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Only update if within character limit or if deleting
    if (newValue.length <= MAX_MESSAGE_LENGTH || newValue.length < message.length) {
      setMessage(newValue);
    }
  };

  return (
    <div className="relative w-full">
      {/* Emoji Picker Popup */}
      {showEmojiPicker && (
        <div className="absolute bottom-full mb-2 left-0 sm:left-auto z-10">
          <div className="shadow-2xl rounded-lg overflow-hidden">
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              width={320}
              height={400}
              theme="dark"
            />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-gray-800 p-2 sm:p-4 rounded-lg">
        <div className="flex items-center gap-2 w-full">
          {/* Emoji Button */}
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
            aria-label="Emoji picker"
          >
            <FaceSmileIcon className="h-6 w-6 text-gray-400 hover:text-blue-400 transition-colors" />
          </button>

          {/* Message Input - changed to textarea for multi-line support */}
          <div className="relative flex-1 min-w-0">
            <textarea
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyPress}
              placeholder="Type your message..."
              rows="1"
              className={`w-full px-4 py-2 border bg-gray-700 text-white border-gray-600 rounded-2xl focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
                isAtLimit ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
              }`}
              style={{ maxHeight: '100px', minHeight: '42px' }}
            />
            
            {/* Character counter */}
            <div 
              className={`absolute bottom-1 right-3 text-xs ${
                isAtLimit ? 'text-red-500 font-bold' :
                isNearLimit ? 'text-amber-500' : 'text-gray-500'
              }`}
            >
              {characterCount}/{MAX_MESSAGE_LENGTH}
            </div>
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!message.trim() || isAtLimit}
            className={`p-3 rounded-full transition-all duration-200 flex-shrink-0 ${
              message.trim() && !isAtLimit
                ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/30'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            aria-label="Send message"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </form>
    </div>
  );
}

export default MessageInput;